class MarketingPopup {
  constructor(container) {
    this.container = container;
    this.form = container.querySelector('#MarketingPopupForm');
    this.formState = container.querySelector('[data-popup-form-state]');
    this.emailInput = container.querySelector('#MarketingPopupEmail');
    this.error = container.querySelector('[data-popup-error]');
    this.result = container.querySelector('[data-popup-result]');
    this.emailStep = container.querySelector('[data-popup-email-step]');
    this.scratchSection = container.querySelector('[data-popup-scratch]');
    this.scratchCard = container.querySelector('[data-popup-scratch-card]');
    this.scratchPrize = container.querySelector('[data-popup-prize]');
    this.scratchLead = container.querySelector('[data-popup-scratch-lead]');
    this.canvas = container.querySelector('[data-popup-canvas]');
    this.foil = container.querySelector('[data-popup-foil]');
    this.particles = container.querySelector('[data-popup-particles]');
    this.discountTargets = container.querySelectorAll('[data-popup-discount]');
    this.codeTargets = container.querySelectorAll('[data-popup-code]');
    this.resultEmailTargets = container.querySelectorAll('[data-popup-result-email]');
    this.copyButton = container.querySelector('[data-popup-copy]');
    this.copyLabel = container.querySelector('[data-popup-copy-label]');
    this.copyStatus = container.querySelector('[data-popup-copy-status]');
    this.tagsInput = container.querySelector('[data-popup-tags]');
    this.closeButtons = container.querySelectorAll('[data-popup-close]');
    this.sessionDismissKey = 'stadiart-marketing-popup-session-dismissed';
    this.claimedKey = 'stadiart-marketing-popup-claimed';
    this.pendingRewardKey = 'stadiart-marketing-popup-pending-reward-v3';
    this.claimedEmailsKey = 'stadiart-marketing-popup-claimed-emails';
    this.delay = Number(container.dataset.delaySeconds || 5) * 1000;
    this.discountOptions = (container.dataset.discountOptions || '12')
      .split(',')
      .map((value) => Number(value.trim()))
      .filter(Boolean);
    this.boundKeydownHandler = this.handleKeydown.bind(this);
    this.isSubmitting = false;
    this.isScratching = false;
    this.scratchCompleted = false;
    this.scratchCheckScheduled = false;
    this.lastParticleAt = 0;
    this.lastHapticAt = 0;
    this.pointerId = null;
    this.ctx = null;
    this.lastScratchPoint = null;
    this.reward = null;
    this.previouslyFocused = null;
    this.wasPrivacyBlocked = false;
    this.scrollLockState = null;
    this.backgroundStates = [];
    this.copyStatusTimer = null;
  }

  init() {
    if (this.shouldForceShow()) {
      this.resetStoredState();
    }

    if (!this.form) return;

    this.closeButtons.forEach((button) => button.addEventListener('click', () => this.close(true)));
    this.form.addEventListener('submit', (event) => this.handleSubmit(event));
    this.scratchCard?.addEventListener('keydown', (event) => this.handleScratchCardKeydown(event));
    this.copyButton?.addEventListener('click', () => this.copyDiscountCode());
    this.emailInput?.addEventListener('input', () => this.clearEmailError());

    const formStatus = this.formState?.dataset.status || 'idle';
    if (formStatus === 'success') {
      const storedReward = this.normalizeReward(this.readPendingReward());
      if (!storedReward) return;
      this.handleSuccessfulSubmission(storedReward);
      return;
    }

    if (formStatus === 'error') {
      this.restoreEmailStepAfterError();
      return;
    }

    if (this.wasDismissed()) return;

    window.setTimeout(() => {
      if (!this.wasDismissed()) this.openCurrentStep();
    }, this.delay);
  }

  wasDismissed() {
    return (
      window.sessionStorage.getItem(this.sessionDismissKey) === 'true' ||
      window.localStorage.getItem(this.claimedKey) === 'true'
    );
  }

  open(focusTarget = 'scratch') {
    if (this.container.hidden) {
      this.previouslyFocused = document.activeElement;
    }

    this.container.hidden = false;
    this.lockPage();
    this.suspendBackground();
    document.addEventListener('keydown', this.boundKeydownHandler, true);

    window.requestAnimationFrame(() => {
      if (focusTarget === 'email') {
        this.emailInput?.focus();
        return;
      }

      if (focusTarget === 'result') {
        this.result?.focus();
        return;
      }

      this.setupScratchCard();
      this.scratchCard?.focus();
    });
  }

  close(remember = false) {
    this.container.hidden = true;
    this.resumeBackground();
    this.unlockPage();
    document.removeEventListener('keydown', this.boundKeydownHandler, true);
    if (remember) window.sessionStorage.setItem(this.sessionDismissKey, 'true');
    if (this.previouslyFocused instanceof HTMLElement) this.previouslyFocused.focus();
  }

  lockPage() {
    if (this.scrollLockState) return;

    const root = document.documentElement;
    const body = document.body;
    this.scrollLockState = {
      scrollX: window.scrollX,
      scrollY: window.scrollY,
      rootOverflow: root.style.overflow,
      rootOverscrollBehavior: root.style.overscrollBehavior,
      bodyPosition: body.style.position,
      bodyTop: body.style.top,
      bodyLeft: body.style.left,
      bodyRight: body.style.right,
      bodyWidth: body.style.width,
      bodyOverflow: body.style.overflow,
      bodyOverscrollBehavior: body.style.overscrollBehavior,
    };

    root.classList.add('marketing-popup-open');
    body.classList.add('marketing-popup-open');
    root.style.overflow = 'hidden';
    root.style.overscrollBehavior = 'none';
    body.style.position = 'fixed';
    body.style.top = `-${this.scrollLockState.scrollY}px`;
    body.style.left = `-${this.scrollLockState.scrollX}px`;
    body.style.right = '0';
    body.style.width = '100%';
    body.style.overflow = 'hidden';
    body.style.overscrollBehavior = 'none';
  }

  unlockPage() {
    if (!this.scrollLockState) return;

    const root = document.documentElement;
    const body = document.body;
    const state = this.scrollLockState;
    root.classList.remove('marketing-popup-open');
    body.classList.remove('marketing-popup-open');
    root.style.overflow = state.rootOverflow;
    root.style.overscrollBehavior = state.rootOverscrollBehavior;
    body.style.position = state.bodyPosition;
    body.style.top = state.bodyTop;
    body.style.left = state.bodyLeft;
    body.style.right = state.bodyRight;
    body.style.width = state.bodyWidth;
    body.style.overflow = state.bodyOverflow;
    body.style.overscrollBehavior = state.bodyOverscrollBehavior;
    this.scrollLockState = null;
    window.scrollTo(state.scrollX, state.scrollY);
  }

  suspendBackground() {
    if (this.backgroundStates.length) return;

    this.backgroundStates = Array.from(document.body.children)
      .filter((element) => element instanceof HTMLElement && element !== this.container)
      .map((element) => ({ element, inert: element.inert }));

    this.backgroundStates.forEach(({ element }) => {
      element.inert = true;
    });
  }

  resumeBackground() {
    this.backgroundStates.forEach(({ element, inert }) => {
      element.inert = inert;
    });
    this.backgroundStates = [];
  }

  handleKeydown(event) {
    if (event.key === 'Escape') {
      event.preventDefault();
      event.stopImmediatePropagation();
      return;
    }

    if (event.key !== 'Tab') return;

    const focusable = Array.from(
      this.container.querySelectorAll(
        'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
      )
    ).filter((element) => element.getClientRects().length > 0 && element.getAttribute('aria-hidden') !== 'true');

    if (!focusable.length) {
      event.preventDefault();
      return;
    }

    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    const active = document.activeElement;

    if (event.shiftKey && (active === first || !this.container.contains(active))) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && (active === last || !this.container.contains(active))) {
      event.preventDefault();
      first.focus();
    }
  }

  handleScratchCardKeydown(event) {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    event.preventDefault();
    this.completeScratch();
  }

  openCurrentStep() {
    const nativePrivacyVisible = Array.from(
      document.querySelectorAll('#shopify-pc__banner, .shopify-pc__banner__dialog, [data-shopify-cookie-banner]')
    ).some((element) => {
      const style = window.getComputedStyle(element);
      const rect = element.getBoundingClientRect();
      return !element.hidden
        && element.getAttribute('aria-hidden') !== 'true'
        && style.display !== 'none'
        && style.visibility !== 'hidden'
        && rect.width > 0
        && rect.height > 0;
    });

    if (document.body.classList.contains('sa-cookie-consent-open') || nativePrivacyVisible) {
      this.wasPrivacyBlocked = true;
      window.setTimeout(() => {
        if (!this.wasDismissed()) this.openCurrentStep();
      }, 1000);
      return;
    }

    if (this.wasPrivacyBlocked) {
      this.wasPrivacyBlocked = false;
      window.setTimeout(() => {
        if (!this.wasDismissed()) this.openCurrentStep();
      }, 5000);
      return;
    }

    const reward = this.getOrCreateReward();
    this.applyReward(reward);

    if (reward.stage === 'email' || reward.stage === 'submitting') {
      reward.stage = 'email';
      this.storeReward(reward);
      this.showEmailStep();
      this.open('email');
      return;
    }

    this.showScratchStep();
    this.open('scratch');
  }

  handleSubmit(event) {
    event.preventDefault();
    if (this.isSubmitting) return;

    const email = this.emailInput?.value?.trim();
    if (!email || !this.isValidEmail(email) || !this.emailInput.checkValidity()) {
      this.error.textContent = 'Zadej prosím platný e-mail ve správném formátu.';
      this.error.hidden = false;
      this.emailInput.setAttribute('aria-invalid', 'true');
      this.emailInput.focus();
      return;
    }

    if (this.hasClaimedEmail(email)) {
      this.error.textContent = 'Tenhle e-mail už los v tomto zařízení získal.';
      this.error.hidden = false;
      this.emailInput.setAttribute('aria-invalid', 'true');
      this.emailInput.focus();
      return;
    }

    this.clearEmailError();
    this.isSubmitting = true;
    this.form.classList.add('marketing-popup__form--submitting');
    this.form.querySelector('[type="submit"]')?.setAttribute('disabled', 'disabled');

    const reward = this.getOrCreateReward();
    const { discount, code, claimId } = reward;
    const tagValue = `newsletter,popup-sleva,scratch-claim-v2-${claimId}-${discount}-${code}`;

    if (this.tagsInput) {
      this.tagsInput.value = tagValue;
    }

    this.reward = { ...reward, email, tagValue, stage: 'submitting' };
    this.storeReward(this.reward);

    this.form.submit();
  }

  clearEmailError() {
    if (!this.emailInput || !this.error) return;
    this.emailInput.removeAttribute('aria-invalid');
    this.error.hidden = true;
  }

  async copyDiscountCode() {
    const code = this.reward?.code || this.codeTargets[0]?.textContent?.trim();
    if (!code) return;

    let copied = false;
    try {
      await navigator.clipboard.writeText(code);
      copied = true;
    } catch (error) {
      copied = this.copyWithFallback(code);
    }

    if (!copied) {
      this.setCopyStatus('Kód se nepodařilo zkopírovat. Označ ho prosím ručně.', false);
      return;
    }

    this.setCopyStatus('Zkopírováno do schránky.', true);
  }

  copyWithFallback(code) {
    const textarea = document.createElement('textarea');
    textarea.value = code;
    textarea.setAttribute('readonly', '');
    textarea.style.position = 'fixed';
    textarea.style.opacity = '0';
    document.body.appendChild(textarea);
    textarea.select();

    let copied = false;
    try {
      copied = document.execCommand('copy');
    } catch (error) {
      copied = false;
    }

    textarea.remove();
    return copied;
  }

  setCopyStatus(message, wasCopied) {
    if (this.copyStatus) this.copyStatus.textContent = message;
    if (this.copyLabel) this.copyLabel.textContent = wasCopied ? 'Zkopírováno' : 'Kopírovat';
    this.copyButton?.classList.toggle('marketing-popup__copy--copied', wasCopied);
    window.clearTimeout(this.copyStatusTimer);
    this.copyStatusTimer = window.setTimeout(() => {
      if (this.copyLabel) this.copyLabel.textContent = 'Kopírovat';
      this.copyButton?.classList.remove('marketing-popup__copy--copied');
    }, 2400);
  }

  getOrCreateReward() {
    const storedReward = this.normalizeReward(this.readPendingReward());
    if (storedReward) {
      this.reward = storedReward;
      return storedReward;
    }

    const discount = this.pickDiscount();
    const reward = {
      version: 2,
      discount,
      code: this.generateDiscountCode(),
      claimId: this.generateClaimId(),
      stage: 'scratch',
    };

    this.reward = reward;
    this.storeReward(reward);
    return reward;
  }

  normalizeReward(reward) {
    if (!reward || typeof reward !== 'object') return null;

    const discount = Number(reward.discount);
    const code = typeof reward.code === 'string' ? reward.code.trim() : '';
    const claimId = typeof reward.claimId === 'string' ? reward.claimId.trim() : '';
    const hasMatchingCode = /^[ABCDEFGHJKLMNPQRSTUVWXYZ23456789]{6}$/.test(code);
    const hasSortableClaimId = /^[0-9A-HJKMNP-TV-Z]{26}$/.test(claimId);
    if (
      reward.version !== 2
      || !this.discountOptions.includes(discount)
      || !hasMatchingCode
      || !hasSortableClaimId
    ) return null;

    const supportedStages = ['scratch', 'email', 'submitting'];
    const fallbackStage = reward.email ? 'email' : 'scratch';
    const stage = supportedStages.includes(reward.stage) ? reward.stage : fallbackStage;
    return { ...reward, discount, code, claimId, stage };
  }

  storeReward(reward) {
    window.sessionStorage.setItem(this.pendingRewardKey, JSON.stringify(reward));
  }

  applyReward(reward, revealCode = false) {
    this.discountTargets.forEach((target) => {
      target.textContent = String(reward.discount);
    });
    this.codeTargets.forEach((target) => {
      target.textContent = revealCode ? reward.code : '';
    });
  }

  pickDiscount() {
    if (!this.discountOptions.length) return 12;
    const randomValues = new Uint32Array(1);
    const range = 0x100000000;
    const limit = Math.floor(range / this.discountOptions.length) * this.discountOptions.length;
    do {
      this.getCrypto().getRandomValues(randomValues);
    } while (randomValues[0] >= limit);
    const index = randomValues[0] % this.discountOptions.length;
    return this.discountOptions[index];
  }

  getCrypto() {
    if (!window.crypto || typeof window.crypto.getRandomValues !== 'function') {
      throw new Error('Secure random values are unavailable.');
    }
    return window.crypto;
  }

  generateSecureToken(length, alphabet) {
    const randomValues = new Uint8Array(length);
    this.getCrypto().getRandomValues(randomValues);
    return Array.from(randomValues, (value) => alphabet[value & 31]).join('');
  }

  generateDiscountCode() {
    const alphabet = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
    return this.generateSecureToken(6, alphabet);
  }

  generateClaimId() {
    const alphabet = '0123456789ABCDEFGHJKMNPQRSTVWXYZ';
    let timestamp = Date.now();
    let timestampPart = '';
    for (let index = 0; index < 10; index += 1) {
      timestampPart = alphabet[timestamp % 32] + timestampPart;
      timestamp = Math.floor(timestamp / 32);
    }
    return `${timestampPart}${this.generateSecureToken(16, alphabet)}`;
  }

  handleSuccessfulSubmission(storedReward) {
    const email = storedReward.email || '';

    this.reward = storedReward;
    this.applyReward(storedReward, true);
    this.resultEmailTargets.forEach((target) => {
      target.textContent = email;
    });
    this.emailStep.hidden = true;
    this.scratchSection.hidden = true;
    this.result.hidden = false;
    this.container.dataset.completed = 'true';
    window.localStorage.setItem(this.claimedKey, 'true');
    if (email) this.rememberClaimedEmail(email);
    window.sessionStorage.removeItem(this.sessionDismissKey);
    window.sessionStorage.removeItem(this.pendingRewardKey);
    this.open('result');
  }

  restoreEmailStepAfterError() {
    const reward = this.getOrCreateReward();
    reward.stage = 'email';
    this.reward = reward;
    this.storeReward(reward);
    this.applyReward(reward);
    this.showEmailStep();
    this.open('email');
  }

  showScratchStep() {
    this.scratchCompleted = false;
    this.container.dataset.completed = 'false';
    this.scratchSection.hidden = false;
    this.emailStep.hidden = true;
    this.result.hidden = true;
    this.canvas.hidden = false;
    if (this.foil) this.foil.hidden = false;
    this.scratchPrize?.setAttribute('aria-hidden', 'true');
    if (this.scratchLead) {
      this.scratchLead.textContent = 'Setři stříbrnou vrstvu a odhal, jaká náhodná sleva ti padla.';
    }
  }

  showEmailStep() {
    this.scratchCompleted = true;
    this.container.dataset.completed = 'true';
    this.scratchSection.hidden = false;
    this.emailStep.hidden = false;
    this.result.hidden = true;
    this.canvas.hidden = true;
    if (this.foil) this.foil.hidden = true;
    this.scratchPrize?.setAttribute('aria-hidden', 'false');
    if (this.emailInput && !this.emailInput.value && this.reward?.email) {
      this.emailInput.value = this.reward.email;
    }
    if (this.scratchLead) {
      this.scratchLead.textContent = 'Výhra je odkrytá. Teď si vyzvedni slevový kód.';
    }
  }

  setupScratchCard() {
    if (!this.canvas || !this.scratchCard || this.ctx) return;

    const rect = this.scratchCard.getBoundingClientRect();
    const dpr = window.devicePixelRatio || 1;
    const width = Math.max(Math.round(rect.width), 1);
    const height = Math.max(Math.round(rect.height), 1);

    this.canvas.width = width * dpr;
    this.canvas.height = height * dpr;
    this.canvas.style.width = `${width}px`;
    this.canvas.style.height = `${height}px`;

    this.ctx = this.canvas.getContext('2d', { willReadFrequently: true });
    this.ctx.scale(dpr, dpr);
    this.ctx.lineCap = 'round';
    this.ctx.lineJoin = 'round';
    this.paintScratchSurface(width, height);

    this.canvas.addEventListener('pointerdown', (event) => this.handleScratchStart(event));
    this.canvas.addEventListener('pointermove', (event) => this.handleScratchMove(event));
    this.canvas.addEventListener('pointerup', () => this.handleScratchEnd());
    this.canvas.addEventListener('pointercancel', () => this.handleScratchEnd());
    this.canvas.addEventListener('pointerleave', () => this.handleScratchEnd());
  }

  paintScratchSurface(width, height) {
    if (!this.ctx) return;

    this.ctx.globalCompositeOperation = 'source-over';
    const gradient = this.ctx.createLinearGradient(0, 0, width, height);
    gradient.addColorStop(0, '#eef3f8');
    gradient.addColorStop(0.5, '#c5cfdb');
    gradient.addColorStop(1, '#9099a8');
    this.ctx.fillStyle = gradient;
    this.ctx.fillRect(0, 0, width, height);

    for (let index = 0; index < 240; index += 1) {
      const x = Math.random() * width;
      const y = Math.random() * height;
      const alpha = 0.08 + Math.random() * 0.16;
      const size = 1 + Math.random() * 2;
      this.ctx.fillStyle = `rgba(255,255,255,${alpha})`;
      this.ctx.fillRect(x, y, size, size);
    }

    this.ctx.fillStyle = 'rgba(255, 255, 255, 0.32)';
    this.ctx.fillRect(0, height * 0.18, width, height * 0.08);
    this.ctx.fillRect(0, height * 0.62, width, height * 0.06);
  }

  handleScratchStart(event) {
    if (this.scratchCompleted || !this.ctx) return;
    event.preventDefault();
    this.isScratching = true;
    this.pointerId = event.pointerId;
    this.lastScratchPoint = this.getScratchPoint(event);
    this.canvas.setPointerCapture?.(event.pointerId);
    this.foil?.classList.add('marketing-popup__scratch-foil--active');
    this.eraseAtPoint(this.lastScratchPoint);
    this.emitHaptic(event);
  }

  handleScratchMove(event) {
    if (!this.isScratching || this.pointerId !== event.pointerId) return;
    event.preventDefault();
    this.eraseAtEvent(event);
    this.emitHaptic(event);
  }

  handleScratchEnd() {
    this.isScratching = false;
    this.pointerId = null;
    this.lastScratchPoint = null;
  }

  getScratchPoint(event) {
    const rect = this.canvas.getBoundingClientRect();
    return {
      x: event.clientX - rect.left,
      y: event.clientY - rect.top,
    };
  }

  eraseAtPoint(point) {
    if (!point) return;

    this.ctx.save();
    this.ctx.globalCompositeOperation = 'destination-out';

    if (this.lastScratchPoint) {
      this.ctx.lineWidth = 42;
      this.ctx.beginPath();
      this.ctx.moveTo(this.lastScratchPoint.x, this.lastScratchPoint.y);
      this.ctx.lineTo(point.x, point.y);
      this.ctx.stroke();
    }

    this.ctx.beginPath();
    this.ctx.arc(point.x, point.y, 21, 0, Math.PI * 2);
    this.ctx.fill();
    this.ctx.restore();

    this.emitParticles(point.x, point.y);
    this.scheduleScratchCheck();
    this.lastScratchPoint = point;
  }

  eraseAtEvent(event) {
    this.eraseAtPoint(this.getScratchPoint(event));
  }

  emitHaptic(event) {
    if (event.pointerType !== 'touch' || typeof navigator.vibrate !== 'function') return;

    const now = performance.now();
    if (now - this.lastHapticAt < 72) return;
    this.lastHapticAt = now;
    navigator.vibrate(7);
  }

  emitParticles(x, y) {
    if (!this.particles) return;

    const now = Date.now();
    if (now - this.lastParticleAt < 24) return;
    this.lastParticleAt = now;

    for (let index = 0; index < 4; index += 1) {
      const particle = document.createElement('span');
      const angle = Math.random() * Math.PI * 2;
      const distance = 18 + Math.random() * 28;
      const driftX = Math.cos(angle) * distance;
      const driftY = Math.sin(angle) * distance - 10;
      const size = 4 + Math.random() * 7;

      particle.className = 'marketing-popup__particle';
      particle.style.left = `${x}px`;
      particle.style.top = `${y}px`;
      particle.style.width = `${size}px`;
      particle.style.height = `${size}px`;
      particle.style.setProperty('--drift-x', `${driftX}px`);
      particle.style.setProperty('--drift-y', `${driftY}px`);
      particle.style.transform = `translate(-50%, -50%) rotate(${Math.random() * 180}deg)`;
      this.particles.appendChild(particle);
      window.setTimeout(() => particle.remove(), 700);
    }
  }

  scheduleScratchCheck() {
    if (this.scratchCheckScheduled || this.scratchCompleted) return;
    this.scratchCheckScheduled = true;

    window.setTimeout(() => {
      this.scratchCheckScheduled = false;
      this.checkScratchProgress();
    }, 60);
  }

  checkScratchProgress() {
    if (!this.ctx || this.scratchCompleted) return;

    const { width, height } = this.canvas;
    const pixels = this.ctx.getImageData(0, 0, width, height).data;
    let transparentCount = 0;

    for (let index = 3; index < pixels.length; index += 16) {
      if (pixels[index] === 0) transparentCount += 1;
    }

    const ratio = transparentCount / (pixels.length / 4 / 4);
    // Reveal the reward and email form after a short scratch, before the amount is fully uncovered.
    if (ratio >= 0.18) {
      this.completeScratch();
    }
  }

  completeScratch() {
    if (this.scratchCompleted) return;

    const reward = this.getOrCreateReward();
    reward.stage = 'email';
    this.reward = reward;
    this.storeReward(reward);
    this.scratchCompleted = true;
    this.canvas.classList.add('marketing-popup__scratch-canvas--cleared');
    this.foil?.classList.add('marketing-popup__scratch-foil--done');
    this.ctx?.clearRect(0, 0, this.canvas.width, this.canvas.height);
    this.scratchPrize?.setAttribute('aria-hidden', 'false');
    if (this.scratchLead) {
      this.scratchLead.textContent = 'Výhra je odkrytá. Teď si vyzvedni slevový kód.';
    }
    this.emailStep.hidden = false;
    this.result.hidden = true;
    this.container.dataset.completed = 'true';

    window.setTimeout(() => {
      this.canvas.hidden = true;
      if (this.foil) this.foil.hidden = true;
      this.emailInput?.focus();
    }, 360);
  }

  getClaimedEmails() {
    try {
      const rawEmails = window.localStorage.getItem(this.claimedEmailsKey);
      const emails = rawEmails ? JSON.parse(rawEmails) : [];
      return Array.isArray(emails) ? emails : [];
    } catch (error) {
      return [];
    }
  }

  hasClaimedEmail(email) {
    const normalizedEmail = email.trim().toLowerCase();
    return this.getClaimedEmails().includes(normalizedEmail);
  }

  isValidEmail(email) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/i.test(email.trim());
  }

  rememberClaimedEmail(email) {
    const normalizedEmail = email.trim().toLowerCase();
    const emails = this.getClaimedEmails();
    if (emails.includes(normalizedEmail)) return;
    emails.push(normalizedEmail);
    window.localStorage.setItem(this.claimedEmailsKey, JSON.stringify(emails));
  }

  readPendingReward() {
    try {
      const rawReward = window.sessionStorage.getItem(this.pendingRewardKey);
      return rawReward ? JSON.parse(rawReward) : null;
    } catch (error) {
      return null;
    }
  }

  shouldForceShow() {
    const searchParams = new URLSearchParams(window.location.search);
    return searchParams.get('show_popup') === '1';
  }

  resetStoredState() {
    window.sessionStorage.removeItem(this.sessionDismissKey);
    window.sessionStorage.removeItem(this.pendingRewardKey);
    window.localStorage.removeItem(this.claimedKey);
  }
}

document.addEventListener('DOMContentLoaded', () => {
  const popup = document.getElementById('MarketingPopup');
  if (!popup) return;

  new MarketingPopup(popup).init();
});
